import Typo from "@/src/components/atom/Typo";
import PlanSelectionGrid, {
  PaymentCycle,
} from "@/src/components/organism/PlanSelectionGrid";
import ExporterMainLayout from "@/src/components/template/Layout/exporter/ExporterMainLayout";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import colorSet from "@/src/styles/color";
import {
  useGetPlansQuery,
  useGetPaymentMethodsQuery,
} from "@/src/store/apis/subscription";
import type {
  PlanDto,
  PlanVersionDto,
} from "@/src/store/apis/subscription/interface";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import SubscriptionPlanModal from "./dialog/SubscriptionPlanModal";


function SubscriptionPlanPage() {
  const { t } = useTranslation([
    "subscriptionPlanSignup",
    "subscriptionManagement",
    "common",
  ]);
  const navigate = useNavigate();

  const [paymentCycle, setPaymentCycle] = useState<PaymentCycle>("MONTHLY");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanDto | null>(null);
  const [selectedPlanVersion, setSelectedPlanVersion] =
    useState<PlanVersionDto | null>(null);

  // API에서 플랜 정보 가져오기
  const { data: plansData, isLoading } = useGetPlansQuery(
    {
      planType: "NORMAL",
    },
  );

  // API에서 기존 결제 수단 정보 가져오기
  const { data: paymentMethodsData } = useGetPaymentMethodsQuery(
    { page: 1, pageSize: 1 },
  );

  // 첫 번째 결제 수단 정보 (대표 결제 수단)
  const existingPaymentMethod = paymentMethodsData?.rows?.[0];

  // 플랜 목록 데이터
  const plans = plansData?.rows || [];


  const handleSelectPlan = useCallback(
    (plan: PlanDto, planVersion: PlanVersionDto) => {
      setSelectedPlan(plan);
      setSelectedPlanVersion(planVersion);
      setIsModalOpen(true);
    },
    [],
  );

  const handleContactSales = useCallback(() => {
    navigate(`${EXPORTER_PRIVATE_PATH.ENTERPRISE_SIGNUP}?isInquiry=true`);
  }, [navigate]);

  const handlePaymentCycleChange = useCallback((cycle: PaymentCycle) => {
    setPaymentCycle(cycle);
  }, []);

  return (
    <ExporterMainLayout
      breadcrumb={[
        t("sideNav:workspaceSettings"),
        t("sideNav:subscription"),
        t("subscriptionPlanSignup:title"),
      ]}
      pageTitle={t("subscriptionPlanSignup:title")}
    >
      <Container>
        {/* Content */}
        <ContentWrapper>
          <ContentCard>
            {/* Section Title */}
            <SectionHeader>
              <SectionTitle>
                {t("subscriptionPlanSignup:planInformation")}
              </SectionTitle>
            </SectionHeader>

            <ContentBody>
              {/* Description */}
              <DescriptionSection>
                <MainTitle>
                  {t("subscriptionPlanSignup:selectSubscriptionPlan")}
                </MainTitle>
                <Description>
                  {t("subscriptionPlanSignup:planDescription")}
                </Description>
              </DescriptionSection>

              {/* Plan Selection Grid */}
              <PlanSelectionGrid
                plans={plans}
                paymentCycle={paymentCycle}
                onPaymentCycleChange={handlePaymentCycleChange}
                onPlanSelect={handleSelectPlan}
                onEnterpriseContact={handleContactSales}
                isLoading={isLoading}
              />
            </ContentBody>
          </ContentCard>
        </ContentWrapper>
      </Container>

      {/* 구독 플랜 모달 */}
      {selectedPlan && selectedPlanVersion && (
        <SubscriptionPlanModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          plan={selectedPlan}
          planVersion={selectedPlanVersion}
          paymentCycle={paymentCycle}
          paymentMethod={existingPaymentMethod}
        />
      )}
    </ExporterMainLayout>
  );
}

export default SubscriptionPlanPage;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: white;
`;

const ContentWrapper = styled.div`
  padding: 0;
`;

const ContentCard = styled.div`
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  padding: 8px;
`;

const SectionHeader = styled.div`
  padding: 12px 16px;
  background: ${colorSet.gray11};
  border-radius: 8px 8px 0 0;
`;

const SectionTitle = styled(Typo)`
  color: ${colorSet.gray2};
  font-size: 17px;
  font-weight: 700;
  line-height: 24px;
`;

const ContentBody = styled.div`
  padding: 24px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  background: white;
  border-radius: 0 0 8px 8px;
`;

const DescriptionSection = styled.div`
  max-width: 544px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MainTitle = styled(Typo)`
  color: ${colorSet.gray2};
  font-size: 26px;
  font-weight: 700;
  line-height: 38px;
`;

const Description = styled(Typo)`
  color: ${colorSet.gray5};
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  white-space: pre-line;
`;
